import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { Icon, Typography } from '@/components';

type RejectedCardProps = {
  readonly mode: 'seller' | 'buyer';
  readonly companyName: string;
  readonly feedbackReject: string | null;
  readonly ratingComments: string | null; // legacy field, fallback for older offers
  readonly internalNotesReject: string | null;
  readonly status?: string;
};

export function RejectedCard({
  mode,
  companyName,
  feedbackReject,
  ratingComments,
  internalNotesReject,
  status,
}: RejectedCardProps) {
  const { t } = useTranslation(['offers']);
  const feedback = feedbackReject ?? ratingComments;
  const isSeller = mode === 'seller';
  const isInternalRejected = status === 'internal_rejected';

  return (
    <Card className="mb-6 border border-red-200 bg-red-50/30 backdrop-blur-sm">
      <CardContent>
        {/* Header */}
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-600">
            <Icon name="x" className="h-6 w-6 text-white" />
          </div>
          <Typography as="h3" size="body-1" className="font-bold">
            {t('EoI declined')}
          </Typography>
          <Typography size="body-3" className="mt-1 text-slate-500">
            {isSeller
              ? t('The prospective buyer has been notified.')
              : !isInternalRejected
                ? t('Unfortunately, the seller has declined your Expression of Interest.')
                : t('Unfortunately, your Expression of Interest has been declined.')}
          </Typography>
        </div>
        {!isInternalRejected && <hr className="border-red-100" />}
        {/* Feedback */}
        {!isInternalRejected && (
          <div className="mt-4">
            <Typography as="p" size="body-3" className="mb-1 font-bold tracking-wide text-red-900 uppercase">
              {isSeller
                ? t('MESSAGE TO {{company}}', { company: companyName.toUpperCase() })
                : t('FEEDBACK FROM SELLER')}
            </Typography>
            <Typography size="body-3" className="mb-3 text-slate-500">
              {isSeller
                ? t('Feedback submitted to the prospective buyer.')
                : t('The seller shared the following feedback.')}
            </Typography>

            {feedback ? (
              <div className="rounded-lg border border-red-100 bg-white px-4 py-3">
                <Typography size="body-3" className="text-slate-700 italic">
                  {feedback}
                </Typography>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-red-100 bg-white/50 px-4 py-3">
                <Typography size="body-3" className="text-slate-400 italic">
                  {t('No feedback provided.')}
                </Typography>
              </div>
            )}
          </div>
        )}
        {/* Private notes — seller only */}
        {isSeller && (
          <>
            <hr className="mt-4 border-red-100" />

            <div className="mt-4">
              <Typography as="p" size="body-3" className="mb-1 font-bold tracking-wide text-red-900 uppercase">
                {t('PRIVATE NOTES')}
              </Typography>
              <div className="mb-3 flex items-center gap-1 text-slate-500">
                <Icon name="lock" className="h-3 w-3" />
                <Typography size="body-3">{t('Private notes submitted to the nTeaser team.')}</Typography>
              </div>

              {internalNotesReject ? (
                <div className="rounded-lg border border-red-100 bg-white px-4 py-3">
                  <Typography size="body-3" className="text-slate-700 italic">
                    {internalNotesReject}
                  </Typography>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-red-100 bg-white/50 px-4 py-3">
                  <Typography size="body-3" className="text-slate-400 italic">
                    {t('No private notes added.')}
                  </Typography>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
